// Paid features: the Pro badge, and the prompt shown in place of a feature
// the developer's plan doesn't include.
import type { ReactNode } from "react";
import { developerApi } from "../lib/api";
import { invalidate, useAction, useQuery } from "../lib/query";
import { useClient } from "../lib/session";
import { contactMailto } from "../lib/site";
import { formatDate } from "../lib/time";
import type { Developer, Feature } from "../lib/types";
import { Button, ErrorNotice, useToast } from "./ui";

export function ProBadge() {
  return <span className="badge pro plain">Pro</span>;
}

export const hasFeature = (me: Developer | undefined, f: Feature) => !!me?.features?.includes(f);

/** The signed-in developer, shared by everything that checks the plan. */
export function useMe() {
  const api = developerApi(useClient());
  return useQuery("dev:me", () => api.me());
}

/** Stripe statuses that keep Pro on. */
export const isPaying = (me: Developer) => ["active", "trialing", "past_due"].includes(me.billing?.status ?? "");

/** A one-line description of the plan, e.g. "Pro, renews 2 Nov 2026". */
export function planSummary(me: Developer): string {
  if (me.plan !== "pro") return "Free";
  if (me.plan_trial) return me.plan_expires_at ? `Pro trial, ends ${formatDate(me.plan_expires_at)}` : "Pro trial";
  const b = me.billing;
  if (b && isPaying(me)) {
    if (b.status === "past_due") return "Pro, payment overdue";
    if (b.period_end) return `Pro, ${b.cancel_at_period_end ? "ends" : "renews"} ${formatDate(b.period_end)}`;
    return "Pro";
  }
  return me.plan_expires_at ? `Pro until ${formatDate(me.plan_expires_at)}` : "Pro";
}

/** Sends the browser to a Stripe page. */
function go(url: string) {
  window.location.assign(url);
}

/**
 * The upgrade buttons: pay with Stripe Checkout (or manage an existing
 * subscription), start the free trial, or email us when payment is off.
 */
export function UpgradeActions({ me, size }: { me: Developer; size?: "small" }) {
  const api = developerApi(useClient());
  const toast = useToast();
  const plans = useQuery("plans", () => api.plans());
  const trial = useAction(() => api.startTrial());
  const checkout = useAction(() => api.checkout());
  const portal = useAction(() => api.billingPortal());
  const online = !!plans.data?.billing_enabled;
  const price = plans.data?.data.find((p) => p.id === "pro")?.price_usd_monthly;
  const cls = (primary: boolean) => `btn${primary ? " primary" : ""}${size ? ` ${size}` : ""}`;

  if (isPaying(me)) {
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div>
          <Button size={size} busy={portal.pending} onClick={async () => {
            const r = await portal.run();
            if (r) go(r.url);
          }}>
            Manage billing
          </Button>
        </div>
        <ErrorNotice error={portal.error} />
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
        {online ? (
          <Button variant={me.trial_available ? undefined : "primary"} size={size} busy={checkout.pending} onClick={async () => {
            const r = await checkout.run();
            if (r) go(r.url);
          }}>
            Upgrade to Pro{price ? `, $${price}/month` : ""}
          </Button>
        ) : (
          <a className={cls(!me.trial_available)} href={contactMailto("Upgrade to Pro")}>
            {me.trial_available ? "Talk to us about Pro" : "Upgrade to Pro"}
          </a>
        )}
        {me.trial_available && (
          <Button variant="primary" size={size} busy={trial.pending} onClick={async () => {
            if (await trial.run()) {
              invalidate("dev:me");
              toast("Your 14-day Pro trial has started");
            }
          }}>
            Start 14-day free trial
          </Button>
        )}
      </div>
      <ErrorNotice error={trial.error ?? checkout.error} />
    </div>
  );
}

/** Shown in place of a Pro feature. */
export function UpgradePanel({ me, title, children, preview }: { me: Developer; title: string; children: ReactNode; preview?: ReactNode }) {
  return (
    <div className="upgrade">
      {preview && <div className="upgrade-preview" aria-hidden="true">{preview}</div>}
      <div className="upgrade-card">
        <ProBadge />
        <h2>{title}</h2>
        <div className="muted">{children}</div>
        <p className="small muted">
          {me.trial_available
            ? "Try every Pro feature free for 14 days, no card needed. Or subscribe for $10 a month."
            : "Pro is $10 a month per developer account, for all your games. Cancel any time."}
        </p>
        <UpgradeActions me={me} />
      </div>
    </div>
  );
}
